<script setup lang="ts">
useCacheRoute()
</script>

<template>
  <div v-track="{ id: 'web-gdpr', view: { value: { page: 'other-gdpr' } } }" class="content">
    <h1 class="title">
      GDPR Privacy Notice
    </h1>
    <p>
      At JUST OKAY LIMITED ("JUST OKAY”), we are committed to
      safeguarding the privacy of personal data. This privacy notice explains
      how JUST OKAY processes your personal data, and your rights in relation to
      the personal data we hold.
    </p>
    <h2>Does this GDPR Privacy Notice apply to me?</h2>
    <ul>
      <li>This GDPR Privacy Notice applies to you if:</li>
      <li>
        You are a "Person” or "Data Subject”—meaning a natural person, not a
        corporation, partnership, or other legal entity—who is physically
        present in the EEA;
      </li>
      <li>
        It is with respect to your "Personal Information”—meaning any
        information relating to an identified or identifiable person—that is
        provided while you are physically present in the EEA;
      </li>
    </ul>
    <h2>The types of information we collect:</h2>
    <ul>
      <li>
        Under the European Union’s General Data Protection Regulation (GDPR),
        personal data is defined as:
      </li>
      <li>
        "Any information relating to an identified or identifiable natural
        person ('data subject'); an identifiable natural person is one who can
        be identified, directly or indirectly, in particular by reference to an
        identifier such as a name, an identification number, location data, an
        online identifier or to one or more factors specific to the physical,
        physiological, genetic, mental, economic, cultural or social identity of
        that natural person”.
      </li>
      <li>The personal data that JUST OKAY collects about you includes:</li>
      <li>IP address;</li>
      <li>Device identifiers;</li>
      <li>Web browser information;</li>
      <li>App activity such as app installation, app uninstallation;</li>
      <li>
        Browser cookies and other technologies such as pixel tags and web
        beacons;
      </li>
      <li>Location information;</li>
      <li>Log data (e.g. access times, hardware and software information);</li>
      <li>Device event information such as crashes and system activity;</li>
      <li>Metadata concerning your Android apps;</li>
      <li>
        We are required or authorized by applicable laws to collect and process
        in order to authenticate or identify you or the information you have
        provided.
      </li>
    </ul>
    <h2>How we process information about you</h2>
    <ul>
      <li>Under the GDPR, processing is defined as:</li>
      <li>
        "Any operation of set of operations which is performed on personal data
        or on sets of personal data, whether or not by automated means, such as
        collection, recording, organization,
      </li>
      <li>
        structuring, storage, adaptation or alteration, retrieval, consultation,
        use, disclosure by transmission, dissemination or otherwise making
        available, alignment or combination, restriction, erasure or
        destruction.”
      </li>
      <li>
        We process Personal Data to operate, improve, understand and personalize
        our services. For example, we use Personal Data to:
      </li>

      <li>Create and manage your profiles;</li>
      <li>Analyze how you use our services;</li>
      <li>Optimize our website content;</li>
      <li>Inform you of the scope of services offered;</li>
      <li>Communicate with you;</li>
      <li>Deal with any concerns or feedback you may have;</li>
      <li>Resolve disputes;</li>
      <li>Prevent and detect crimes;</li>
      <li>Meet contract or legal obligations</li>
      <li>
        For any other purpose for which you provide us with your personal data.
      </li>
    </ul>
    <h2>Sharing your personal information</h2>
    <p>
      For the purposes referred to in this privacy notice and relying on the
      bases for processing as set forth above, we may share your personal data
      with certain third parties. You are given the opportunity to opt-in to
      some of these data sharing agreements, where JUST OKAY does not have a
      legitimate basis, or contractual purpose for sharing the data. JUST OKAY
      will not share your personal data with third parties unless it is
      necessary for the legitimate interests of JUST OKAY in providing you
      services, or where it is necessary for the purposes of entering into or
      carrying out a contract between you and JUST OKAY. Where the sharing of data
      would not be necessary for the legitimate interests of JUST OKAY or
      necessary for contractual purposes, we will obtain your consent before
      sharing your data with said third parties.
    </p>
    <p>
      Any third parties that we may share your data with are obliged to keep
      your details secure, and to use them only to fulfill the service they
      provide you on our behalf.
    </p>
    <h2>Your rights as a data subject</h2>
    <ul>
      <li>
        At any point while JUST OKAY is in possession of, or processing your
        personal data, you, the data subject, have the following rights:
      </li>
      <li>
        Right of access – you have the right to request a copy of the
        information that we hold about you.
      </li>
      <li>
        Right of rectification – you have a right to correct data that we hold
        about you that is inaccurate or incomplete.
      </li>
      <li>
        Right to be forgotten – in certain circumstances you can ask for the
        data we hold about you to be erased from our records.
      </li>
      <li>
        Right to restriction of processing – where certain conditions apply, you
        have a right to restrict the processing.
      </li>
      <li>
        Right of portability – you have the right to have the data we hold about
        you transferred to another organization.
      </li>
      <li>
        Right to object – you have the right to object to certain types of
        processing such as direct marketing.
      </li>
      <li>
        Right to object to automated processing – you also have the right to be
        subject to the legal effects of automated processing.
      </li>
      <li>
        Right to judicial review: in the event that JUST OKAY refuses your request
        under any of the above rights, we will provide you with a reason as to
        why.
      </li>
    </ul>
    <h2>Under what circumstances will JUST OKAY contact me?</h2>
    <p>
      Our aim is not to be intrusive, and we undertake not to ask irrelevant or
      unnecessary questions. Moreover, the information you provide will be
      subject to rigorous measures and procedures to minimize the risk of
      unauthorized access or disclosure. Notwithstanding this, JUST OKAY will
      contact you if we identify that a breach affecting your personal data has
      occurred.
    </p>
    <h2>How to contact us</h2>
    <p>
      If you have any questions related to this GDPR Privacy Notice, please
      contact us via support@ldplayer.net
    </p>
    <h2>Changes and amendments to this policy</h2>
    <p>
      From time to time, we may update or amend this GDPR Privacy Notice to
      reflect new or different privacy practices, without notice to you.
      However, if these changes are material, we will place a notice on our
      website.
    </p>
  </div>
</template>

<style lang="scss" scoped>
.content {
  margin-left: auto;
  margin-right: auto;
  max-width: 1440px;
  padding: 80px;

  .title {
    font-size: 26px;
    font-weight: 700;
    text-align: center;
    margin: 0 auto 60px;
    padding: 60px 0;
    border-bottom-width: 1px;
    border-color: #eaeaea;
    border-style: solid;
  }

  p {
    font-size: 14px;
    line-height: 24px;
    color: #666;
    margin-bottom: 25px;
  }

  ul {
    margin-bottom: 25px;
  }

  li {
    font-size: 14px;
    line-height: 24px;
    color: #666;
    margin-inline-start: 20px;
    list-style-type: disc;
  }

  h2 {
    font-size: 16px;
    line-height: 28px;
    font-weight: 700;
    margin: 0;
    margin-bottom: 5px;
  }
}

/* layer: default */
@media (max-width: 767.9px) {
  .content {
    padding: 20px;

    .title{
      margin: 0 auto 20px;
      padding: 20px 0;
    }
  }
}
</style>
